/**
 * Building blocks shared by the strategies: the two ways to take work off a
 * guard (hand the whole shift over, or give up its start or end) expressed as
 * change operations, plus the breaks those changes leave owing.
 */

import { planBreakWindow } from '../breakPlanner';
import {
  overlaps,
  requiredBreakMinutes,
  requiredLicenceIds,
  windowMinutes,
} from '../rosterModel';
import type {
  GuardShift,
  ProposedFix,
  RankedCandidate,
  RemediationContext,
  RosterChangeOperation,
  TimeWindow,
} from '../types';
import {
  colleagueBreakWindows,
  planOwedBreak,
  siblingPlannedBreaks,
  type BreakPlanningContext,
} from './breakCoordination';

/** The board will not create a duty shorter than this, so neither will a trim. */
export const MIN_KEPT_MINUTES = 30;
const MIN_RELEASED_MINUTES = 15;

const shiftWindow = (shift: GuardShift): TimeWindow => ({ startMinute: shift.startMinute, endMinute: shift.endMinute });

/** Moves every duty and break the guard holds in the shift to another guard. */
export function reassignShiftOperations(shift: GuardShift, toGuardId: string): RosterChangeOperation[] {
  return [
    ...shift.duties.map((duty): RosterChangeOperation => ({
      op: 'reassign_duty',
      duty_slot_id: duty.id,
      from_guard_id: shift.guardId,
      to_guard_id: toGuardId,
    })),
    ...shift.breaks.map((breakSlot): RosterChangeOperation => ({
      op: 'transfer_break',
      break_slot_id: breakSlot.id,
      from_guard_id: shift.guardId,
      to_guard_id: toGuardId,
    })),
  ];
}

export interface ReleaseRequest {
  context: BreakPlanningContext;
  shift: GuardShift;
  /** Which end of the shift the guard gives up. */
  side: 'start' | 'end';
  /** The guard's new start (side `start`) or finish (side `end`). */
  boundaryMinute: number;
  handoverGuardId: string | null;
}

export function releasedWindow(shift: GuardShift, side: 'start' | 'end', boundaryMinute: number): TimeWindow {
  return side === 'start'
    ? { startMinute: shift.startMinute, endMinute: boundaryMinute }
    : { startMinute: boundaryMinute, endMinute: shift.endMinute };
}

export function keptWindow(shift: GuardShift, side: 'start' | 'end', boundaryMinute: number): TimeWindow {
  return side === 'start'
    ? { startMinute: boundaryMinute, endMinute: shift.endMinute }
    : { startMinute: shift.startMinute, endMinute: boundaryMinute };
}

/** Whether a cut at `boundaryMinute` leaves a usable shift on both sides. */
export function isUsableBoundary(shift: GuardShift, side: 'start' | 'end', boundaryMinute: number): boolean {
  const kept = keptWindow(shift, side, boundaryMinute);
  const released = releasedWindow(shift, side, boundaryMinute);
  return windowMinutes(kept) >= MIN_KEPT_MINUTES && windowMinutes(released) >= MIN_RELEASED_MINUTES;
}

const overlapMinutes = (a: TimeWindow, b: TimeWindow): number => Math.max(
  0,
  Math.min(a.endMinute, b.endMinute) - Math.max(a.startMinute, b.startMinute),
);

const within = (inner: TimeWindow, outer: TimeWindow): boolean => (
  inner.startMinute >= outer.startMinute && inner.endMinute <= outer.endMinute
);

/**
 * The guard gives up one end of the shift. Duties wholly in the released part
 * move to the handover guard (or are left open); the duty spanning the cut is
 * split. Breaks follow the time they sit in, and whoever ends up short of the
 * break the rule demands gets one planned for them.
 */
export function releaseShiftPartOperations(request: ReleaseRequest): RosterChangeOperation[] {
  const { context, shift, side, boundaryMinute, handoverGuardId } = request;
  const { rule } = context;
  const released = releasedWindow(shift, side, boundaryMinute);
  const kept = keptWindow(shift, side, boundaryMinute);
  const operations: RosterChangeOperation[] = [];

  let keptDutyMinutes = 0;
  let releasedDutyMinutes = 0;

  shift.duties.forEach((duty) => {
    const dutyWindow = { startMinute: duty.start_minute, endMinute: duty.end_minute };
    keptDutyMinutes += overlapMinutes(dutyWindow, kept);
    releasedDutyMinutes += overlapMinutes(dutyWindow, released);

    if (within(dutyWindow, released)) {
      operations.push(handoverGuardId
        ? { op: 'reassign_duty', duty_slot_id: duty.id, from_guard_id: shift.guardId, to_guard_id: handoverGuardId }
        : { op: 'remove_duty', duty_slot_id: duty.id, guard_id: shift.guardId });
    } else if (duty.start_minute < boundaryMinute && duty.end_minute > boundaryMinute) {
      operations.push({
        op: 'split_duty',
        duty_slot_id: duty.id,
        guard_id: shift.guardId,
        at_minute: boundaryMinute,
        guard_keeps: side === 'start' ? 'after' : 'before',
        handover_guard_id: handoverGuardId,
      });
    }
  });

  let keptBreakMinutes = 0;
  let handoverBreakMinutes = 0;
  const keptBreaks: TimeWindow[] = [];

  shift.breaks.forEach((breakSlot) => {
    const breakWindow = { startMinute: breakSlot.start_minute, endMinute: breakSlot.end_minute };
    if (within(breakWindow, kept)) {
      keptBreakMinutes += windowMinutes(breakWindow);
      keptBreaks.push(breakWindow);
    } else if (within(breakWindow, released) && handoverGuardId) {
      handoverBreakMinutes += windowMinutes(breakWindow);
      operations.push({ op: 'transfer_break', break_slot_id: breakSlot.id, from_guard_id: shift.guardId, to_guard_id: handoverGuardId });
    } else {
      // Released with nobody to take it, or straddling the cut (breaks cannot be
      // split): the break goes, and any shortfall is re-planned below.
      operations.push({ op: 'remove_break', break_slot_id: breakSlot.id, guard_id: shift.guardId });
    }
  });

  // The shift is being reshaped, so its breaks keep clear of every colleague's
  // break: those rostered, and those planned for colleagues short of one.
  const colleagueBreaks = [
    ...colleagueBreakWindows(context.snapshot, shift),
    ...siblingPlannedBreaks(shift, context, 'all'),
  ];

  const keptShortfall = requiredBreakMinutes(rule, keptDutyMinutes) - keptBreakMinutes;
  if (keptShortfall > 0) {
    const window = planBreakWindow({ shift: kept, durationMinutes: keptShortfall, avoid: [...colleagueBreaks, ...keptBreaks] });
    if (window) {
      operations.push({ op: 'add_break', guard_id: shift.guardId, start_minute: window.startMinute, end_minute: window.endMinute, is_paid: false });
    }
  }

  if (handoverGuardId) {
    const handoverShortfall = requiredBreakMinutes(rule, releasedDutyMinutes) - handoverBreakMinutes;
    if (handoverShortfall > 0) {
      const window = planBreakWindow({ shift: released, durationMinutes: handoverShortfall, avoid: colleagueBreaks });
      if (window) {
        operations.push({ op: 'add_break', guard_id: handoverGuardId, start_minute: window.startMinute, end_minute: window.endMinute, is_paid: false });
      }
    }
  }

  return operations;
}

async function eligibleCandidates(
  context: RemediationContext,
  window: TimeWindow,
  shift: GuardShift,
  limit: number,
): Promise<RankedCandidate[]> {
  if (limit <= 0) return [];
  const duties = shift.duties.filter((duty) => overlaps({ startMinute: duty.start_minute, endMinute: duty.end_minute }, window));
  const ranked = await context.findCandidates({
    ...window,
    requiredLicenceTypeIds: requiredLicenceIds(duties),
    excludeGuardIds: [shift.guardId],
  });
  return ranked.filter((candidate) => candidate.eligible).slice(0, limit);
}

/**
 * "Give the whole shift to X" for the best few eligible guards. If the shift is
 * short of its break, the replacement gets the missing break too — otherwise the
 * handover would only move the breach to someone else.
 */
export async function reassignShiftProposals(
  shift: GuardShift,
  context: RemediationContext,
  options: { limit: number; preference: number },
): Promise<ProposedFix[]> {
  const window = shiftWindow(shift);
  const candidates = await eligibleCandidates(context, window, shift, options.limit);

  // The replacement takes the break slot the original guard would have been
  // given, so it stays clear of the breaks suggested for colleagues on the post.
  const owedBreak = planOwedBreak(shift, context).topUp;

  return candidates.map((candidate, index) => ({
    kind: 'reassign_shift',
    guardId: shift.guardId,
    guardName: shift.guardName,
    window,
    newBoundaryMinute: null,
    candidate,
    uncoveredMinutes: 0,
    preference: options.preference + index,
    operations: [
      ...reassignShiftOperations(shift, candidate.guard.guardId),
      ...(owedBreak
        ? [{
            op: 'add_break' as const,
            guard_id: candidate.guard.guardId,
            start_minute: owedBreak.startMinute,
            end_minute: owedBreak.endMinute,
            is_paid: false,
          }]
        : []),
    ],
  }));
}

/**
 * "Start later / finish earlier" for the guard: one proposal per suggested
 * handover guard, then one that leaves the released time open on the board.
 */
export async function releaseShiftProposals(
  shift: GuardShift,
  context: RemediationContext,
  options: { side: 'start' | 'end'; boundaryMinute: number; handoverLimit: number; preference: number },
): Promise<ProposedFix[]> {
  const { side, boundaryMinute } = options;
  if (!isUsableBoundary(shift, side, boundaryMinute)) return [];

  const released = releasedWindow(shift, side, boundaryMinute);
  const build = (candidate: RankedCandidate | null, preference: number): ProposedFix => ({
    kind: candidate
      ? (side === 'start' ? 'handover_start' : 'handover_end')
      : (side === 'start' ? 'trim_start' : 'trim_end'),
    guardId: shift.guardId,
    guardName: shift.guardName,
    window: released,
    newBoundaryMinute: boundaryMinute,
    candidate,
    uncoveredMinutes: candidate ? 0 : windowMinutes(released),
    preference,
    operations: releaseShiftPartOperations({
      context,
      shift,
      side,
      boundaryMinute,
      handoverGuardId: candidate?.guard.guardId ?? null,
    }),
  });

  const candidates = await eligibleCandidates(context, released, shift, options.handoverLimit);
  return [
    ...candidates.map((candidate, index) => build(candidate, options.preference + index)),
    build(null, options.preference + candidates.length + 1),
  ];
}
